import { maxLength, propertyMapping, required } from '@hakla/typed-forms'
import { OfferEvent, OfferResponse, ResourceVariant } from '@/api/generated'
import { updateOrAppend } from '@/shared/utils/list'
import { compareBy } from '@/shared/utils/compare-by'
import { OfferPartner } from '@/business/offers/models/offer-partner'
import { format, isBefore, parse, parseISO, setHours, setMinutes } from 'date-fns'
import { isNotBlank } from '@/shared/utils/is-blank'
import { $each } from '@/shared/forms/validations/$each'
import * as vuelidate from 'vuelidate/lib/validators'
import { Validator } from '@/shared/forms/validator'
import { OfferEventForm } from '@/business/offers/models/offer-event-form'

export interface OfferForm {
  audiences: string[]
  categories: string[]
  description?: string
  events: OfferEventForm[]
  format?: string
  header: ResourceVariant[]
  id?: number
  logo: ResourceVariant
  name: string
  partners: OfferPartner[]
  sector?: string
  shortDescription?: string
  type?: string
  visible: boolean
}

function dateBefore(other: string): Validator {
  return ((value: string, form: any) => {
    let valid = true

    const fromRaw = value
    const untilRaw = form[other]

    if (isNotBlank(fromRaw) && isNotBlank(untilRaw)) {
      const from = parse(fromRaw, 'HH:mm', 0)
      const until = parse(untilRaw, 'HH:mm', 0)

      if (isNaN(from.getTime()) === false && isNaN(until.getTime()) === false) {
        valid = isBefore(from, until)
      }
    }

    return valid
  }) as any
}

export const offerFormMapping = function() {
  return propertyMapping<OfferResponse, OfferForm>(({ generic }) => {
    return {
      audiences: generic('audiences'),
      categories: generic('categories'),
      events: generic('events')
        .deserializeWith(value => {
          return (value.events ?? [])
            .slice()
            .sort((a, b) => a.dateFrom.getTime() - b.dateFrom.getTime())
            .map(event => {
              return {
                date: event.dateFrom ? format(event.dateFrom, 'yyyy-MM-dd') : '',
                from: event.dateFrom ? format(event.dateFrom, 'HH:mm') : '',
                id: event.id,
                location: event.location,
                until: event.dateUntil ? format(event.dateUntil, 'HH:mm') : '',
              }
            })
        })
        .serializeWith((model, form) => {
          model.events = form.events.map(event => {
            const mappedEvent: Partial<OfferEvent> = {
              id: event.id,
              location: event.location,
            }

            if (isNotBlank(event.date)) {
              const date = parseISO(event.date)

              if (isNotBlank(event.from)) {
                const parsed = parse(event.from, 'HH:mm', 0)

                mappedEvent.dateFrom = setHours(
                  setMinutes(date, parsed.getMinutes()),
                  parsed.getHours()
                )
              } else {
                mappedEvent.dateFrom = date
              }

              if (isNotBlank(event.until)) {
                const parsed = parse(event.until, 'HH:mm', 0)

                mappedEvent.dateUntil = setHours(
                  setMinutes(date, parsed.getMinutes()),
                  parsed.getHours()
                )
              } else {
                mappedEvent.dateUntil = undefined
              }
            } else {
              mappedEvent.dateFrom = undefined
              mappedEvent.dateUntil = undefined
            }

            return mappedEvent as OfferEvent
          })
        })
        .validations(
          $each({
            date: {
              required: vuelidate.required,
            },
            from: {
              dateBefore: dateBefore('until'),
              pattern: vuelidate.helpers.regex('pattern', /^([0-9]|[01]\d|2[0-3])(:[0-5]\d)?$/),
              required: vuelidate.required,
            },
            location: {
              required: vuelidate.required,
            },
            until: {
              required: vuelidate.required,
              pattern: vuelidate.helpers.regex('pattern', /^([0-9]|[01]\d|2[0-3])(:[0-5]\d)?$/),
            },
          })
        ),
      description: generic('description'),
      format: generic('format'),
      id: generic('id'),
      header: generic('header')
        .initializeWith([])
        .deserializeWith(value => {
          return value.resources.find(resource => resource.name === 'header')?.resources || []
        })
        .serializeWith((model, form) => {
          model.resources = updateOrAppend(
            model.resources,
            {
              name: 'header',
              resources: form.header,
            },
            compareBy('name')
          )
        }),

      logo: generic('logo')
        .deserializeWith(value => {
          return value.resources.find(resource => resource.name === 'logo')?.resources[0]
        })
        .serializeWith((model, form) => {
          model.resources = updateOrAppend(
            model.resources,
            { name: 'logo', resources: [form.logo] },
            compareBy('name')
          )
        }),

      name: generic('name').validations(required),

      partners: generic('partners')
        .initializeWith([])
        .deserializeWith(value => {
          return value.partners.map(partner => {
            return {
              date: partner.date.toISOString().substring(0, 10),
              organisation: partner.organisation,
            } as OfferPartner
          })
        })
        .serializeWith((model, form) => {
          model.partners = form.partners.map(partner => {
            return {
              date: new Date(partner.date),
              organisation: partner.organisation,
            }
          })
        }),

      sector: generic('sector'),
      shortDescription: generic('shortDescription').validations(maxLength(350)),
      type: generic('type'),
      visible: generic('visible'),
    }
  })
}
